import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Modal, Radio } from "antd";
import styles from "./EmailBuilder.module.scss";
import {
  createColumns,
  createBlockFor,
  createDefaultLayout,
  EMAIL_CONTENT_WIDTH,
  EMAIL_FONT,
  EmailBlock,
  EmailBlockType,
  EmailBuilderValue,
  EmailLayout,
  MOBILE_CONTENT_WIDTH,
  MOBILE_PREVIEW_WIDTH,
} from "./emailBuilder.model";
import {
  BlockPath,
  cloneLayout,
  containerOf,
  duplicateBlock,
  getBlock,
  getContainer,
  insertBlock,
  moveBlock,
  removeBlock,
  samePath,
} from "./emailBuilder.tree";
import { blockToHtml, generateEmailHtml } from "./generateEmailHtml";
import RichText from "./RichText";
import BlockSettings from "./BlockSettings";
import { EmailTemplateLanguageType } from "../../models/EmailTemplate";
import { EmailTemplateService } from "../../services/email-template.service";
import useAppDispatch from "../../hooks/useAppDispatch";
import { handleError } from "../../helper/errorHandler";

type ContentBlockType = Exclude<EmailBlockType, "columns">;

const CONTENT_BLOCKS: ContentBlockType[] = [
  "heading",
  "paragraph",
  "image",
  "button",
  "divider",
  "spacer",
];
const BLOCK_LABELS: Record<EmailBlockType, string> = {
  heading: "Heading",
  paragraph: "Paragraph",
  image: "Image",
  button: "Button",
  divider: "Divider",
  spacer: "Spacer",
  columns: "Columns",
};

type Drag =
  | { source: "palette"; type: ContentBlockType }
  | { source: "layout"; count: 1 | 2 | 3 }
  | { source: "existing"; path: BlockPath };

type DropTarget = { path: BlockPath; position: "before" | "after" } | { container: BlockPath };

// The HTML a value is sent as
export const getEmailBuilderHtml = (value: EmailBuilderValue) =>
  value.mode === "blocks" ? generateEmailHtml(value.layout) : value.html;

interface IEmailBuilderProps {
  value: EmailBuilderValue;
  onChange: (value: EmailBuilderValue) => void;
  language: EmailTemplateLanguageType;
  // What the server replaces in this email (by subject); others become empty
  constants: string[];
}

// Remount it (key) when another template / language is loaded - undo history belongs to one template
const EmailBuilder = ({ value, onChange, language, constants }: IEmailBuilderProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [selectedPath, setSelectedPath] = useState<BlockPath | null>(null);
  const [undoStack, setUndoStack] = useState<EmailBuilderValue[]>([]);
  const [redoStack, setRedoStack] = useState<EmailBuilderValue[]>([]);
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [codeHtml, setCodeHtml] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewWidth, setPreviewWidth] = useState<"desktop" | "mobile">("desktop");
  const drag = useRef<Drag | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  // One undo step per typing session in a text, not per keystroke
  const textSnapshotTaken = useRef(false);

  const layout: EmailLayout | null = value.mode === "blocks" ? value.layout : null;
  const selectedBlock = layout ? getBlock(layout, selectedPath) : null;

  const commit = (next: EmailBuilderValue) => {
    setUndoStack((stack) => [...stack, value]);
    setRedoStack([]);
    textSnapshotTaken.current = false;
    onChange(next);
  };

  // Changes the layout on a clone; the mutation may return the path to select
  const updateLayout = (mutate: (draft: EmailLayout) => BlockPath | null | void) => {
    if (!layout) return;
    const draft = cloneLayout(layout);
    const nextPath = mutate(draft);
    if (nextPath !== undefined) setSelectedPath(nextPath);
    commit({ mode: "blocks", layout: draft });
  };

  const undo = () => {
    if (!undoStack.length) return;
    setRedoStack((stack) => [...stack, value]);
    setUndoStack((stack) => stack.slice(0, -1));
    setSelectedPath(null);
    onChange(undoStack[undoStack.length - 1]);
  };

  const redo = () => {
    if (!redoStack.length) return;
    setUndoStack((stack) => [...stack, value]);
    setRedoStack((stack) => stack.slice(0, -1));
    setSelectedPath(null);
    onChange(redoStack[redoStack.length - 1]);
  };

  // Ctrl+Z / Ctrl+Y outside of inputs and texts (they have their own undo)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("input, textarea, [contenteditable='true']")) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // ---- texts

  const onTextFocus = (path: BlockPath) => {
    setSelectedPath(path);
    if (!textSnapshotTaken.current) {
      setUndoStack((stack) => [...stack, value]);
      setRedoStack([]);
      textSnapshotTaken.current = true;
    }
  };

  const onTextChange = (path: BlockPath, html: string) => {
    if (!layout) return;
    const draft = cloneLayout(layout);
    (getBlock(draft, path) as EmailBlock & { text: string }).text = html;
    onChange({ mode: "blocks", layout: draft });
  };

  const syncSelectedText = () => {
    if (!selectedPath) return;
    const element = document.querySelector(`[data-rich-text="${selectedPath.join("-")}"]`);
    if (element) onTextChange(selectedPath, element.innerHTML);
  };

  // ---- drag & drop (ported from TrainYourPulse)

  const canDropInto = (container: BlockPath) => {
    const current = drag.current;
    if (!current || !layout) return false;
    const isRoot = container.length === 0;
    if (current.source === "layout") return isRoot;
    if (current.source === "existing") {
      if (getBlock(layout, current.path)?.type === "columns" && !isRoot) return false;
      // Not into a column of the columns block being dragged
      if (!isRoot && current.path.length === 1 && current.path[0] === container[0]) return false;
    }
    return true;
  };

  const startDrag = (e: React.DragEvent, value: Drag) => {
    e.stopPropagation();
    drag.current = value;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", value.source);
  };

  const endDrag = () => {
    drag.current = null;
    setDropTarget(null);
  };

  const drop = (container: BlockPath, index: number) => {
    const current = drag.current;
    endDrag();
    if (!current || !layout) return;
    updateLayout((draft) => {
      if (current.source === "existing")
        return insertBlock(draft, container, index, null, current.path);
      const block =
        current.source === "palette"
          ? createBlockFor(current.type, constants)
          : createColumns(current.count);
      return insertBlock(draft, container, index, block, null);
    });
  };

  const onBlockDragOver = (e: React.DragEvent, path: BlockPath) => {
    if (!canDropInto(containerOf(path))) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const position = e.clientY > rect.top + rect.height / 2 ? "after" : "before";
    if (
      !dropTarget ||
      !("path" in dropTarget) ||
      !samePath(dropTarget.path, path) ||
      dropTarget.position !== position
    ) {
      setDropTarget({ path, position });
    }
  };

  const onBlockDrop = (e: React.DragEvent, path: BlockPath) => {
    if (!dropTarget || !("path" in dropTarget) || !canDropInto(containerOf(path))) return;
    e.preventDefault();
    e.stopPropagation();
    drop(containerOf(path), path[path.length - 1] + (dropTarget.position === "after" ? 1 : 0));
  };

  const onContainerDragOver = (e: React.DragEvent, container: BlockPath) => {
    if (!canDropInto(container)) return;
    e.preventDefault();
    e.stopPropagation();
    if (!dropTarget || !("container" in dropTarget) || !samePath(dropTarget.container, container)) {
      setDropTarget({ container });
    }
  };

  const onContainerDrop = (e: React.DragEvent, container: BlockPath) => {
    if (!layout || !canDropInto(container)) return;
    e.preventDefault();
    e.stopPropagation();
    drop(container, getContainer(layout, container).length);
  };

  const isDropMark = (path: BlockPath, position: "before" | "after") =>
    !!dropTarget &&
    "path" in dropTarget &&
    samePath(dropTarget.path, path) &&
    dropTarget.position === position;

  const isContainerTarget = (container: BlockPath) =>
    !!dropTarget && "container" in dropTarget && samePath(dropTarget.container, container);

  // ---- HTML mode

  const openCodeEditor = () => setCodeHtml(getEmailBuilderHtml(value));

  const applyCode = () => {
    if (codeHtml === null) return;
    setSelectedPath(null);
    commit({ mode: "html", html: codeHtml });
    setCodeHtml(null);
  };

  const importHtml = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedPath(null);
      commit({ mode: "html", html: String(reader.result || "") });
    };
    reader.readAsText(file);
  };

  const buildWithBlocks = () =>
    Modal.confirm({
      title: t("BuildWithBlocks"),
      content: t("BuildWithBlocksConfirm"),
      okText: t("Confirm"),
      cancelText: t("Cancel"),
      onOk: () => commit({ mode: "blocks", layout: createDefaultLayout(constants) }),
    });

  const openPreview = async () => {
    try {
      const res = await EmailTemplateService.previewEmailTemplate(
        getEmailBuilderHtml(value),
        language
      );
      setPreviewHtml(res.data.html);
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  // ---- canvas

  const renderBlockContent = (block: EmailBlock, path: BlockPath) => {
    if (block.type === "heading" || block.type === "paragraph") {
      const isHeading = block.type === "heading";
      return (
        <div
          style={{
            padding: block.padding,
            background: block.backgroundColor,
            textAlign: block.align,
          }}
        >
          <RichText
            tag={isHeading ? "h1" : "p"}
            html={block.text}
            pathKey={path.join("-")}
            editable
            onFocus={() => onTextFocus(path)}
            onChange={(html) => onTextChange(path, html)}
            style={{
              margin: 0,
              fontSize: block.fontSize,
              lineHeight: isHeading ? 1.3 : 1.5,
              color: block.color,
              fontWeight: isHeading ? "bold" : undefined,
              fontFamily: EMAIL_FONT,
              textAlign: block.align,
            }}
          />
        </div>
      );
    }
    if (block.type === "columns") {
      return (
        <div className={styles.columns} style={{ background: block.backgroundColor }}>
          {block.columns.map((column, ci) => {
            const container = [path[0], ci];
            return (
              <div
                key={ci}
                className={`${styles.column} ${isContainerTarget(container) ? styles.dropInto : ""}`}
                style={{ width: `${column.widthPercent}%` }}
                onDragOver={(e) => onContainerDragOver(e, container)}
                onDrop={(e) => onContainerDrop(e, container)}
              >
                {column.blocks.map((child, j) => renderBlock(child, [path[0], ci, j]))}
                {column.blocks.length === 0 && (
                  <div className={styles.emptyColumn}>{t("DropBlocksHere")}</div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    if (block.type === "image" && !block.url) {
      return <div className={styles.imagePlaceholder}>{t("UploadImage")}</div>;
    }
    return <div dangerouslySetInnerHTML={{ __html: blockToHtml(block) }} />;
  };

  // The click must not reach the block, which would select it again after the action changed the selection
  const blockAction = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    action();
  };

  const renderBlock = (block: EmailBlock, path: BlockPath): React.ReactNode => {
    const isSelected = samePath(selectedPath, path);
    return (
      <div
        key={block.id}
        className={[
          styles.block,
          isSelected ? styles.selected : "",
          isDropMark(path, "before") ? styles.dropBefore : "",
          isDropMark(path, "after") ? styles.dropAfter : "",
        ].join(" ")}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedPath(path);
        }}
        onDragOver={(e) => onBlockDragOver(e, path)}
        onDrop={(e) => onBlockDrop(e, path)}
      >
        <div className={styles.blockActions}>
          <span
            className={styles.handle}
            draggable
            title={t("Drag")}
            onDragStart={(e) => startDrag(e, { source: "existing", path })}
            onDragEnd={endDrag}
          >
            ⠿ {t(BLOCK_LABELS[block.type])}
          </span>
          <button
            type="button"
            title={t("MoveUp")}
            onClick={blockAction(() => updateLayout((d) => moveBlock(d, path, -1)))}
          >
            ↑
          </button>
          <button
            type="button"
            title={t("MoveDown")}
            onClick={blockAction(() => updateLayout((d) => moveBlock(d, path, 1)))}
          >
            ↓
          </button>
          <button
            type="button"
            title={t("Duplicate")}
            onClick={blockAction(() => updateLayout((d) => duplicateBlock(d, path)))}
          >
            ⧉
          </button>
          <button
            type="button"
            title={t("Delete")}
            onClick={blockAction(() => updateLayout((d) => removeBlock(d, path)))}
          >
            ✕
          </button>
        </div>
        {renderBlockContent(block, path)}
      </div>
    );
  };

  const canvasWidth = viewMode === "mobile" ? MOBILE_CONTENT_WIDTH : EMAIL_CONTENT_WIDTH;

  return (
    <div className={styles.builder}>
      <div className={styles.toolbar}>
        <Button onClick={undo} disabled={!undoStack.length}>
          {t("Undo")}
        </Button>
        <Button onClick={redo} disabled={!redoStack.length}>
          {t("Redo")}
        </Button>
        <Radio.Group
          value={viewMode}
          optionType="button"
          onChange={(e) => setViewMode(e.target.value)}
          options={[
            { value: "desktop", label: t("Desktop") },
            { value: "mobile", label: t("Mobile") },
          ]}
        />
        <div className={styles.toolbarRight}>
          <Button onClick={openPreview}>{t("Preview")}</Button>
          <Button onClick={openCodeEditor}>{t("EditHtml")}</Button>
          <input
            ref={importRef}
            type="file"
            accept=".html,.htm,text/html"
            style={{ display: "none" }}
            onChange={importHtml}
          />
          <Button onClick={() => importRef.current?.click()}>{t("ImportHtml")}</Button>
        </div>
      </div>

      <div className={styles.body}>
        {layout && (
          <div className={styles.palette}>
            <h4>{t("Content")}</h4>
            {CONTENT_BLOCKS.map((type) => (
              <div
                key={type}
                className={styles.paletteItem}
                draggable
                onDragStart={(e) => startDrag(e, { source: "palette", type })}
                onDragEnd={endDrag}
                onClick={() =>
                  updateLayout((d) =>
                    insertBlock(d, [], d.blocks.length, createBlockFor(type, constants), null)
                  )
                }
              >
                {t(BLOCK_LABELS[type])}
              </div>
            ))}
            <h4>{t("Layout")}</h4>
            {([1, 2, 3] as const).map((count) => (
              <div
                key={count}
                className={styles.paletteItem}
                draggable
                onDragStart={(e) => startDrag(e, { source: "layout", count })}
                onDragEnd={endDrag}
                onClick={() =>
                  updateLayout((d) =>
                    insertBlock(d, [], d.blocks.length, createColumns(count), null)
                  )
                }
              >
                {t("ColumnsCount", { count })}
              </div>
            ))}
            <p className={styles.hint}>{t("EmailBuilderPaletteHint")}</p>
          </div>
        )}

        <div className={styles.canvasWrapper} onClick={() => setSelectedPath(null)}>
          {layout ? (
            <div
              className={`${styles.canvas} ${isContainerTarget([]) ? styles.dropInto : ""}`}
              style={{ width: canvasWidth }}
              // Links inside the canvas must not navigate away from the editor
              onClickCapture={(e) => {
                if ((e.target as HTMLElement).closest("a")) e.preventDefault();
              }}
              onDragOver={(e) => onContainerDragOver(e, [])}
              onDrop={(e) => onContainerDrop(e, [])}
            >
              {layout.blocks.map((block, i) => renderBlock(block, [i]))}
              {layout.blocks.length === 0 && (
                <div className={styles.emptyCanvas}>{t("DropBlocksHere")}</div>
              )}
            </div>
          ) : (
            <div className={styles.htmlMode} onClick={(e) => e.stopPropagation()}>
              <div className={styles.htmlModeBar}>
                <span>{t("EmailBuilderHtmlModeHint")}</span>
                <Button onClick={buildWithBlocks}>{t("BuildWithBlocks")}</Button>
              </div>
              <iframe
                title="html"
                className={styles.htmlFrame}
                style={{ width: canvasWidth }}
                srcDoc={`<div style="font-family:${EMAIL_FONT};font-size:16px;color:#101016;">${
                  value.mode === "html" ? value.html : ""
                }</div>`}
              />
            </div>
          )}
        </div>

        {layout && (
          <div className={styles.side}>
            {selectedBlock && selectedPath ? (
              <>
                <h4>{t(BLOCK_LABELS[selectedBlock.type])}</h4>
                <BlockSettings
                  block={selectedBlock}
                  pathKey={selectedPath.join("-")}
                  constants={constants}
                  onTextCommand={syncSelectedText}
                  onChange={(block) =>
                    updateLayout((d) => {
                      getContainer(d, containerOf(selectedPath))[
                        selectedPath[selectedPath.length - 1]
                      ] = block;
                    })
                  }
                />
              </>
            ) : (
              <p className={styles.hint}>{t("EmailBuilderSelectHint")}</p>
            )}
          </div>
        )}
      </div>

      <Modal
        open={codeHtml !== null}
        title={t("EditHtml")}
        width={900}
        okText={t("Apply")}
        cancelText={t("Cancel")}
        onOk={applyCode}
        onCancel={() => setCodeHtml(null)}
      >
        {value.mode === "blocks" && <p className={styles.hint}>{t("EmailBuilderEditHtmlHint")}</p>}
        <Input.TextArea
          value={codeHtml ?? ""}
          onChange={(e) => setCodeHtml(e.target.value)}
          autoSize={{ minRows: 18, maxRows: 30 }}
          className={styles.code}
        />
      </Modal>

      <Modal
        open={previewHtml !== null}
        title={t("Preview")}
        width={760}
        footer={null}
        onCancel={() => setPreviewHtml(null)}
      >
        <Radio.Group
          value={previewWidth}
          optionType="button"
          onChange={(e) => setPreviewWidth(e.target.value)}
          options={[
            { value: "desktop", label: t("Desktop") },
            { value: "mobile", label: t("Mobile") },
          ]}
        />
        <iframe
          title="preview"
          className={styles.previewFrame}
          style={{ width: previewWidth === "mobile" ? MOBILE_PREVIEW_WIDTH : "100%" }}
          srcDoc={previewHtml ?? ""}
        />
      </Modal>
    </div>
  );
};

export default EmailBuilder;
